import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, AfterViewChecked, ChangeDetectorRef, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ChatService, ChatMessage } from '../../../../../shared/services/chat.service';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-ai-chat-modal',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './ai-chat-modal.html',
    styleUrls: ['./ai-chat-modal.scss']
})
export class AiChatModalComponent implements AfterViewChecked, OnChanges, OnDestroy {
    @Input() isOpen: boolean = false;
    @Output() close = new EventEmitter<void>();
    @ViewChild('chatContainer') private chatContainer!: ElementRef;

    userMessage: string = '';
    chatHistory: ChatMessage[] = [];
    isLoading: boolean = false;
    private subs = new Subscription();

    constructor(
        private chatService: ChatService,
        private cdr: ChangeDetectorRef
    ) { }

    suggestedPrompts: string[] = [
        'How can I save more money?',
        'Can you recommend any good credit cards for my income level?',
        'I really want to take a trip. Can you help me chose where and save for it?',
        'If I saved “500INR a week and invested conservatively, how much money would I have saved by my 50th birthday?'
    ];

    ngAfterViewChecked() {
        this.scrollToBottom();
    }

    private scrollToBottom(): void {
        try {
            if (this.chatContainer && this.chatContainer.nativeElement) {
                this.chatContainer.nativeElement.scrollTop = this.chatContainer.nativeElement.scrollHeight;
            }
        } catch (err) { }
    }

    onClose() {
        this.close.emit();
    }

    onPromptClick(prompt: string) {
        this.userMessage = prompt;
        this.sendMessage();
    }

    formatMessage(text: string): string {
        if (!text) return '';
        let formatted = text;

        // Headers (e.g., ### Header or ## Header)
        formatted = formatted.replace(/^### (.*?)$/gm, '<h4 style="margin: 12px 0 6px 0; font-size: 15px; font-weight: 700; color: #1A1C1E;">$1</h4>');
        formatted = formatted.replace(/^## (.*?)$/gm, '<h3 style="margin: 14px 0 8px 0; font-size: 17px; font-weight: 700; color: #1A1C1E;">$1</h3>');

        // Bold (**text**)
        formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

        // Italic (*text*)
        formatted = formatted.replace(/\*(.*?)\*/g, '<em>$1</em>');

        // Bullet points (e.g., * Item or - Item)
        // Match lines starting with * or - followed by space, potentially with leading spaces
        formatted = formatted.replace(/^[ \t]*[*-] (.*?)$/gm, '<div style="display: flex; gap: 8px; margin-bottom: 4px; padding-left: 8px;"><span>•</span><span>$1</span></div>');

        // Numbered lists (e.g., 1. Item)
        formatted = formatted.replace(/^[ \t]*(\d+)\. (.*?)$/gm, '<div style="display: flex; gap: 8px; margin-bottom: 4px; padding-left: 8px;"><span>$1.</span><span>$2</span></div>');

        // Horizontal rule
        formatted = formatted.replace(/^---$/gm, '<hr style="border: none; border-top: 1px solid rgba(0,0,0,0.1); margin: 16px 0;">');

        return formatted;
    }

    sendMessage() {
        const message = this.userMessage.trim();
        if (!message || this.isLoading) return;

        const history = [...this.chatHistory];
        this.chatHistory.push({ role: 'user', content: message });
        this.userMessage = '';
        this.isLoading = true;
        this.cdr.detectChanges();

        this.subs.add(this.chatService.sendMessage(message, history)
            .pipe(
                finalize(() => {
                    this.isLoading = false;
                    this.cdr.detectChanges();
                })
            )
            .subscribe({
                next: (response) => {

                    const aiMessage = response.candidates?.[0]?.content?.parts?.[0]?.text || 'Sorry, I couldn\'t process that.';
                    this.chatHistory.push({
                        role: 'assistant',
                        content: aiMessage,
                        formattedContent: this.formatMessage(aiMessage)
                    });
                },
                error: (error) => {
                    console.error('Error:', error);
                    this.chatHistory.push({
                        role: 'assistant',
                        content: 'Sorry, something went wrong. Please try again.',
                        formattedContent: '<em>Sorry, something went wrong. Please try again.</em>'
                    });
                }
            }));
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['isOpen']) {
            if (this.isOpen) {
                // Prevent body scroll when modal opens
                document.body.style.overflow = 'hidden';
                document.body.style.position = 'fixed';
                document.body.style.width = '100%';
            } else {
                // Restore body scroll when modal closes
                this.resetBodyStyles();
            }
        }
    }

    ngOnDestroy() {
        this.subs.unsubscribe();
        this.resetBodyStyles();
    }

    private resetBodyStyles() {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
    }
}
